import { Injectable } from '@angular/core';
import { Platform } from 'src/app/models/api';
import { ActionTypes } from 'src/app/models/api/playbooks/actions/actionTypes.enum';
import { PlaybookAction, PlaybookStatus, PlaybookTrigger } from 'src/app/models/api/playbooks/playbook.model';
import { TriggerTypes } from 'src/app/models/api/playbooks/triggers/triggerTypes.enum';
import { PlaybookTemplatesDetailsModule } from 'src/app/models/templates/playbooks/playbookTemplateDetails.module';
import { PlaybookTemplateKey } from 'src/app/models/templates/playbooks/playbookTemplateKey.enum';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import { NewViolationFoundTriggerConfigModel } from 'src/app/models/api/playbooks/triggers/newViolationTriggerModel';
import { ResourceStatus } from 'src/app/models/api/inventoryResource.model';
import { SendEmailActionDefinitionModel } from 'src/app/models/api/playbooks/actions/sendEmailProperties';
import { arePlatformActionsAllowed } from 'src/app/services/actions/common/actionsHelpers';
import { PlaybookCreationApiRequestModel } from 'src/app/models/api/playbooks/playbookCreationApiModel';
import { StoreService } from '../../infra/store/store.service';
import { Observable } from 'rxjs/internal/Observable';
import { IntegrationModel, StatusCodeEnum } from 'src/app/models/api/integration.model';
import { PoliciesHttpService } from 'src/app/services/httpServices/governance/policiesHttpService/policies-http.service';
import { emailTemplatesByTriggerType } from 'src/app/components/playbooks/playbook-action/actions/send-email-action/emailTemplates';
import { BehaviorSubject } from 'rxjs';
import { ISelectOption } from 'src/app/models/selectOptions';
import { BuiltinLabelIds } from 'src/app/models/constants/builtinLabelIds.constants';
import { LabelInclusionStatus } from 'src/app/models/api/playbooks/triggers/labelInclusionStatus.enum';
import { RuleTag } from 'src/app/models/api/governance/violationRule.model';
import { PowerplatformCopilotAuthenticationTypeEnum } from 'src/app/components/actions/powerplatform-actions/set-copilot-authentication/enums/enums';

@Injectable({
  providedIn: 'root',
})
export class PlaybookTemplatesService {
  private _playbookTemplatesDetails = PlaybookTemplatesDetailsModule;
  private _mapTemplateIdToActionsAndTrigger: {
    [id in PlaybookTemplateKey]: {
      trigger: PlaybookTrigger;
      actions: PlaybookAction[];
    };
  };
  private _resourceTypeOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>([]);

  constructor(
    private _labelsProviderPipe: LabelsProviderPipe,
    private _store: StoreService,
    private _policiesHttpService: PoliciesHttpService
  ) {
    this._generateTemplateIdToActionsAndTriggerMap();
  }

  /**
   * Generates a playbook creation object according to the values of a playbook template
   * Note: The function returns an observable since it calls integrations and policies values that might requirer a request to the server
   * @param templateId the template id to generate playbook from
   * @returns playbook creation object
   */
  public generatePlaybookFromTemplateId(templateId?: string): Observable<PlaybookCreationApiRequestModel> {
    const response = new Observable<PlaybookCreationApiRequestModel>((observer) => {
      const template = this._playbookTemplatesDetails.filter((t) => t.key == templateId)[0];

      if (!template) {
        observer.error('Template id not found');
      }

      this._selectIntegrationForPlatform(template.platform).subscribe((integrationId) => {
        if (integrationId) {
          this._selectPolicyForIntegration(integrationId).subscribe((policyId) => {
            const playbook: PlaybookCreationApiRequestModel = {
              name: this._labelsProviderPipe.transform(template.titleLabel),
              description: this._labelsProviderPipe.transform(template.descriptionLabel),
              platform: template.platform,
              status: PlaybookStatus.disabled,
              integrationId: integrationId ? integrationId : '',
              actions: this._mapTemplateIdToActionsAndTrigger[template.key].actions,
              trigger: this._mapTemplateIdToActionsAndTrigger[template.key].trigger,
              policyIds: policyId ? [policyId] : [],
            };

            observer.next(playbook);
          });
        } else {
          const playbook: PlaybookCreationApiRequestModel = {
            name: this._labelsProviderPipe.transform(template.titleLabel),
            description: this._labelsProviderPipe.transform(template.descriptionLabel),
            platform: template.platform,
            status: PlaybookStatus.disabled,
            integrationId: integrationId ? integrationId : '',
            actions: this._mapTemplateIdToActionsAndTrigger[template.key].actions,
            trigger: this._mapTemplateIdToActionsAndTrigger[template.key].trigger,
            policyIds: [],
          };
          observer.next(playbook);
        }
      });
    });
    return response;
  }

  public isIntegrationSupportedForPlaybookTemplates(integration: IntegrationModel): boolean {
    return (
      integration.connectionStatus.code == StatusCodeEnum.on &&
      arePlatformActionsAllowed(integration.platform, integration.authenticationMethod)
    );
  }

  public setResourceTypeOptions(resourceTypeOptions: ISelectOption[]): void {
    this._resourceTypeOptions$.next(resourceTypeOptions);
  }

  public getResourceTypeOptions$(): BehaviorSubject<ISelectOption[]> {
    return this._resourceTypeOptions$;
  }

  private _selectIntegrationForPlatform(platform: Platform): Observable<string> {
    const response = new Observable<string>((observer) => {
      this._store.integrations$.subscribe((integrations) => {
        const filteredIntegrations = integrations.filter(
          (integration) =>
            integration.platform == platform && this.isIntegrationSupportedForPlaybookTemplates(integration)
        );
        if (filteredIntegrations.length == 1) {
          observer.next(filteredIntegrations[0].id);
        } else {
          observer.next(undefined);
        }
      });
    });
    return response;
  }

  private _selectPolicyForIntegration(integrationId: string): Observable<string | undefined> {
    const response = new Observable<string | undefined>((observer) => {
      this._policiesHttpService.getIntegrationPolicies(integrationId).subscribe((policies) => {
        if (policies.length == 1) {
          observer.next(policies[0].id);
        } else {
          observer.next(undefined);
        }
      });
    });
    return response;
  }

  private _getSendEmailActionProperties(platform: Platform, contactMaker = false): SendEmailActionDefinitionModel {
    return {
      contactMaker: contactMaker,
      recipients: [],
      subject: emailTemplatesByTriggerType[TriggerTypes.newViolationFound].subject,
      bodyText: emailTemplatesByTriggerType[TriggerTypes.newViolationFound].body(platform),
    };
  }

  private _getNewViolationTriggerConfigurationForActiveResource(
    ruleIds: string[]
  ): NewViolationFoundTriggerConfigModel {
    return {
      ruleUpdatesEnabled: false,
      resourceStatus: [ResourceStatus.active],
      ruleIds: ruleIds,
    };
  }

  private _generateTemplateIdToActionsAndTriggerMap(): void {
    this._mapTemplateIdToActionsAndTrigger = {
      powerplatformSilentRemediation: {
        trigger: {
          triggerType: TriggerTypes.newOrExistingViolationFound,
          configuration: {
            ruleUpdatesEnabled: false,
            ruleIds: [
              'ZN_P00112', // Flow has an insecure sensitive step
              'ZN_P00108', // Flow is exposing sensitive data in logs (via flow trigger)
              'ZN_P00103', // Flow is exposing sensitive data in logs
              'ZN_P00095', // Flow has an insecure Azure Key Vault step
              'ZN_P00099', // Flow run log contains potentially sensitive data (PCI)
              'ZN_P00117', // Flow run log contains potentially sensitive data (PII)
            ],
            ruleTags: [RuleTag.generalAvailability],
            labels: {
              labelsIds: [BuiltinLabelIds.FALSE_POSITIVE],
              inclusionStatus: LabelInclusionStatus.exclude,
            },
          },
        },
        actions: [
          {
            actionType: ActionTypes.secureFlowSteps,
            actionDefinitionProperties: {},
          },
          {
            actionType: ActionTypes.sendEmail,
            actionDefinitionProperties: this._getSendEmailActionProperties(Platform.powerplatform, true),
          },
          {
            actionType: ActionTypes.addLabel,
            actionDefinitionProperties: {
              labelIds: [BuiltinLabelIds.REMEDIATION_PERFORMED],
              description: 'Automatically added by Silent Remediation playbook',
            },
          },
        ],
      },
      powerplatformAgentSetAuthentication: {
        trigger: {
          triggerType: TriggerTypes.newOrExistingViolationFound,
          configuration: {
            ruleUpdatesEnabled: false,
            ruleIds: [
              'ZN_P00131', // Agent accepting unauthenticated chat
              'ZN_P00141', // Agent custom authentication is not enforced
            ],
            ruleTags: [RuleTag.generalAvailability],
            labels: {
              labelsIds: [BuiltinLabelIds.FALSE_POSITIVE],
              inclusionStatus: LabelInclusionStatus.exclude,
            },
          },
        },
        actions: [
          {
            actionType: ActionTypes.setCopilotAuthentication,
            actionDefinitionProperties: {
              authenticationType: PowerplatformCopilotAuthenticationTypeEnum.permittedUsersOnly,
            },
          },
          {
            actionType: ActionTypes.sendEmail,
            actionDefinitionProperties: this._getSendEmailActionProperties(Platform.powerplatform, true),
          },
          {
            actionType: ActionTypes.addLabel,
            actionDefinitionProperties: {
              labelIds: [BuiltinLabelIds.REMEDIATION_PERFORMED],
              description: 'Automatically added by Agent Set Authentication playbook',
            },
          },
        ],
      },
      powerplatformRemoveOrgWideAccess: {
        trigger: {
          triggerType: TriggerTypes.newOrExistingViolationFound,
          configuration: {
            ruleUpdatesEnabled: false,
            ruleIds: [
              'ZN_P00146', // Unpublished Agent is accessible by the entire tenant
              'ZN_P00143', // Published Agent is accessible by the entire tenant
            ],
            ruleTags: [RuleTag.generalAvailability],
            labels: {
              labelsIds: [BuiltinLabelIds.FALSE_POSITIVE],
              inclusionStatus: LabelInclusionStatus.exclude,
            },
          },
        },
        actions: [
          {
            actionType: ActionTypes.removeAccessFromCopilot,
            actionDefinitionProperties: {},
          },
          {
            actionType: ActionTypes.sendEmail,
            actionDefinitionProperties: this._getSendEmailActionProperties(Platform.powerplatform, true),
          },
          {
            actionType: ActionTypes.addLabel,
            actionDefinitionProperties: {
              labelIds: [BuiltinLabelIds.REMEDIATION_PERFORMED],
              description: 'Automatically added by Remove Org-Wide Access playbook',
            },
          },
        ],
      },
      powerplatformRemoveOrgWideAccessApps: {
        trigger: {
          triggerType: TriggerTypes.newOrExistingViolationFound,
          configuration: {
            ruleUpdatesEnabled: false,
            ruleIds: [
              'ZN_P00148', // Agent is consuming uploaded files with potentially sensitive data (PHI)
            ],
            ruleTags: [RuleTag.generalAvailability],
            labels: {
              labelsIds: [BuiltinLabelIds.FALSE_POSITIVE],
              inclusionStatus: LabelInclusionStatus.exclude,
            },
          },
        },
        actions: [
          {
            actionType: ActionTypes.removeAccessFromCopilot,
            actionDefinitionProperties: {},
          },
          {
            actionType: ActionTypes.sendEmail,
            actionDefinitionProperties: this._getSendEmailActionProperties(Platform.powerplatform, true),
          },
          {
            actionType: ActionTypes.addLabel,
            actionDefinitionProperties: {
              labelIds: [BuiltinLabelIds.REMEDIATION_PERFORMED],
              description: 'Automatically added by Remove Org-Wide Access (Apps) playbook',
            },
          },
        ],
      },
      powerplatformRemoveOrgWideAccessConnections: {
        trigger: {
          triggerType: TriggerTypes.newOrExistingViolationFound,
          configuration: {
            ruleUpdatesEnabled: false,
            ruleIds: [
              'ZN_P00046', // Connection is accessible by the entire tenant
            ],
            ruleTags: [RuleTag.generalAvailability],
            labels: {
              labelsIds: [BuiltinLabelIds.FALSE_POSITIVE],
              inclusionStatus: LabelInclusionStatus.exclude,
            },
          },
        },
        actions: [
          {
            actionType: ActionTypes.removeAccess,
            actionDefinitionProperties: {},
          },
          {
            actionType: ActionTypes.sendEmail,
            actionDefinitionProperties: this._getSendEmailActionProperties(Platform.powerplatform, true),
          },
          {
            actionType: ActionTypes.addLabel,
            actionDefinitionProperties: {
              labelIds: [BuiltinLabelIds.REMEDIATION_PERFORMED],
              description: 'Automatically added by Remove Org-Wide Access (Connections) playbook',
            },
          },
        ],
      },
    };
  }
}
